import React, { useMemo, useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { uniq } from 'lodash';
import moment from 'moment-timezone';
import { ReduxState } from 'services/types';
import {
	OrderObject,
	ResponsiblePerson,
	OrderInfo,
	Shopper,
	OrderProduct,
	Shop,
	ProductApi,
	UserDetailName,
	UnitSystemType,
	UserProperty,
	LoadingData,
	Duration,
} from 'common/types';
import { setNewOrder } from 'common/api/frontend';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import {
	getEndDateWithInventoryBlockers,
	getStartDateWithInventoryBlockers,
	updateOrderProducts,
} from 'common/utils/productUtils';
import {
	getNewEndTime,
	getTimeNearestToNextQuarterHour,
	getTotalRentalDurationFromProducts,
} from 'common/utils/dateCalculations';
import {
	detailsIncludeMeasurementUnits,
	getSortedRequiredDetails,
	getUpdatedShopperFromDetailChange,
	getUpdatedShopperFromUnitSystemChange,
} from 'common/utils/userDetails';
import {
	getShopperIdsWithProductsFromShoppers,
	getUniqueProducts,
	sortShoppers,
} from 'common/utils/rentalUtils';
import { getProductCodes } from 'services/utils';
import {
	Theme,
	Typography,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	FormControlLabel,
	FormControl,
	Checkbox,
	Grid,
	TextField,
	Select,
	InputLabel,
	MenuItem,
	Box,
	Switch,
	SelectChangeEvent,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';
import Spinner from 'components/Spinner';
import UnitSystemSelect from 'common/components/UnitSystemSelect';
import UserDetailField from 'common/components/UserDetailField';
import Banner from 'components/Banner';
import StripePattern from 'common/components/StripePattern';

import { useTranslation } from 'services/localization/useTranslation';
import { Routes, push } from 'routing';

import * as RentalSelectors from 'selectors/RentalViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { getOrderPricing, getPricingOptions } from 'common/modules/orders';
import { orderHasDeliveryOrPickup } from 'common/modules/delivery/utils';
import { usePermissions } from 'hooks/usePermissions';
import OverbookedBanner from 'components/OverbookedBanner';
import {
	getOverbookedVariantIdsFromProductTotalAvailabilitiesWithTimes,
	ProductTotalAvailability,
} from 'common/api/frontend/inventory_new';
import { getOpeningTimesForDateWithMoments } from 'common/utils/shopUtils';
import { handleQuantityAndAvailabilityFetch } from 'common/api/frontend/inventory_new/getQuantitiesAndAvailabilities';
import { useHasFeature } from 'hooks';

interface MissingDetails {
	shopperId: string;
	missingDetails: UserDetailName[];
}

interface MissingDetailsFormError {
	[shopperId: string]: {
		[detail: string]: boolean;
	};
}

type DialogContentOptions = 'WHO_RENT' | 'MISSING_DETAILS' | 'RESPONSIBLE_PERSON';

interface OwnProps {
	onClose: (success?: boolean) => void;
	open: boolean;
	rental: OrderObject;
}

interface StateToProps {
	shop: Shop;
	stockProducts: ProductApi[];
}

type Props = OwnProps & StateToProps & WithStyles<typeof styles>;

const RentAgainDialog = (props: Props) => {
	const { classes, open, onClose, rental, shop, stockProducts } = props;
	const { products, rentalInfo, shoppers } = rental;
	const { hasFeature } = useHasFeature();
	const sortedShoppers = sortShoppers(shoppers, rentalInfo.responsiblePerson);
	const shopUnitSystem = useSelector(ShopSelectors.getShopUnitSystem);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const openingHours = useSelector(RentalSelectors.openingHoursWithExceptions);
	const shopTimeZone = useSelector(ShopSelectors.getShopTimeZone);
	const stockProductsMap = useSelector(StockSelectors.stockProductsById);

	const hasDelivery = orderHasDeliveryOrPickup(rental.orderDelivery);

	const [unitSystem, setUnitSystem] = useState(shopUnitSystem);
	const [loading, setLoading] = useState(false);
	const [dialogContent, setDialogContent] = useState<DialogContentOptions>('WHO_RENT');
	const [rentAgainShoppers, setRentAgainShoppers] = useState<Shopper[]>([]);
	const [rentAgainShopperIds, setRentAgainShopperIds] = useState<string[]>(
		sortedShoppers.map((s) => s.id),
	);
	const [missingDetailsShoppers, setMissingDetailsShoppers] = useState<MissingDetails[]>([]);
	const [missingDetailsShopperIndex, setMissingDetailsShopperIndex] = useState<number>(0);
	const [missingDetailsFormError, setMissingDetailsFormError] = useState<MissingDetailsFormError>(
		{},
	);
	const [rentAgainResponsiblePerson, setRentAgainResponsiblePerson] = useState({
		id: '',
		phone: '',
		email: '',
	});
	const [keepProductCodes, setKeepProductCodes] = useState<boolean>(false);
	const [productAvailabilities, setProductAvailabilities] = useState<
		LoadingData<ProductTotalAvailability[] | null>
	>({ data: null, loading: false, error: null });
	const [someRentalOutsideOpeningHours, setSomeRentalOutsideOpeningHours] = useState<boolean>(
		false,
	);
	const useAvailabilities = !hasFeature('DISABLE_PRODUCT_AVAILABILITY_LOADING', { scope: 'shop' });

	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');

	const { t } = useTranslation();

	const rentAgainProducts = useMemo(() => {
		return products
			.filter((product) => rentAgainShopperIds.includes(product.shopperId))
			.map((p) => {
				const updatedStartDate = getTimeNearestToNextQuarterHour().toISOString();
				const duration: Duration = {
					durationInSeconds: p.rentalDurationInSeconds,
					durationName: p.durationName,
					durationType: p.durationType,
				};
				const updatedEndDate =
					openingHours && getNewEndTime(updatedStartDate, duration, openingHours, shopTimeZone);
				return {
					...p,
					startDate: updatedStartDate,
					endDate: updatedEndDate,
				};
			});
	}, [products, rentAgainShopperIds, openingHours, shopTimeZone]);

	const overbookedVariantIds = useMemo(
		() =>
			(productAvailabilities.data &&
				getOverbookedVariantIdsFromProductTotalAvailabilitiesWithTimes(
					productAvailabilities.data,
					rentAgainProducts,
				)) ??
			[],
		[productAvailabilities.data, rentAgainProducts],
	);
	const showBanner = !loading && (!!overbookedVariantIds.length || someRentalOutsideOpeningHours);
	const DisabledCTADueToOverbooking = showBanner && !allowOverbookings;

	useEffect(() => {
		const fetchAvailabilities = async (
			start: moment.Moment,
			end: moment.Moment,
			products: OrderProduct[],
		) => {
			setLoading(true);
			setProductAvailabilities({ data: null, error: null, loading: true });
			const loadProductAvailabilities = await Promise.all(
				getUniqueProducts(products, stockProductsMap).map((product) => {
					const startDateWithInventoryBlockers = getStartDateWithInventoryBlockers({
						startDate: start.toISOString(),
					});
					const endDateWithInventoryBlockers = getEndDateWithInventoryBlockers(
						end.toISOString(),
						product,
						stockProducts,
					);
					return handleQuantityAndAvailabilityFetch({
						product,
						stockProducts,
						startDate: startDateWithInventoryBlockers,
						endDate: endDateWithInventoryBlockers,
						shopId,
						startLocationId: rentalInfo.startLocation.id,
						shopLocations,
						purchaseType: 'rental',
					});
				}),
			);
			setProductAvailabilities({ data: loadProductAvailabilities, error: null, loading: false });
			setLoading(false);
		};
		const startDate = getTimeNearestToNextQuarterHour();
		const duration = getTotalRentalDurationFromProducts(products);
		const longestEndDate = moment(startDate).add(duration.durationInSeconds, 'seconds');
		if (useAvailabilities) {
			fetchAvailabilities(startDate, longestEndDate, products);
		}
	}, [
		products,
		stockProducts,
		stockProductsMap,
		rentalInfo.startLocation.id,
		shopId,
		shopLocations,
		useAvailabilities,
	]);

	useEffect(() => {
		const startDate = getTimeNearestToNextQuarterHour();
		const durationsFromProducts = uniq(
			rentAgainProducts.map((p) => {
				return {
					durationType: p.durationType,
					rentalDurationInSeconds: p.rentalDurationInSeconds,
				};
			}),
		);
		const anyProductReturnIsOutsideOpeningHours = durationsFromProducts.some((p) => {
			const endDate = moment(startDate).add(p.rentalDurationInSeconds, 'seconds');
			const openingTimesForEndDate = getOpeningTimesForDateWithMoments(endDate, openingHours);
			return (
				p.durationType !== 'opening_hours' &&
				(openingTimesForEndDate?.isClosed ||
					endDate.isAfter(openingTimesForEndDate?.closeMoment) ||
					endDate.isBefore(openingTimesForEndDate?.openMoment))
			);
		});
		setSomeRentalOutsideOpeningHours(anyProductReturnIsOutsideOpeningHours);
	}, [rentAgainProducts, openingHours]);

	const toggleSelectRentAgainShoppers = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { id, checked } = event.target;
		if (checked) {
			setRentAgainShopperIds([...new Set([...rentAgainShopperIds, id])]);
			return;
		}
		setRentAgainShopperIds(rentAgainShopperIds.filter((shopperId) => shopperId !== id));
	};

	const handleDetailChange = (
		input: UserProperty | null,
		detail: UserDetailName,
		shopperId: string,
	) => {
		const updatedRentAgainShoppers = rentAgainShoppers.map((s) =>
			s.id === shopperId ? getUpdatedShopperFromDetailChange(s, detail, input) : s,
		);
		setRentAgainShoppers(updatedRentAgainShoppers);
		setMissingDetailsFormError({
			...missingDetailsFormError,
			[shopperId]: { ...missingDetailsFormError[shopperId], [detail]: !input },
		});
	};

	const handleUnitSystemChange = (newUnitSystem: UnitSystemType, shopperId: string) => {
		setUnitSystem(newUnitSystem);
		const updatedRentAgainShoppers = rentAgainShoppers.map((s) =>
			s.id === shopperId ? getUpdatedShopperFromUnitSystemChange(s, newUnitSystem) : s,
		);
		setRentAgainShoppers(updatedRentAgainShoppers);
	};

	const handleSelectResponsiblePerson = (event: SelectChangeEvent) => {
		setRentAgainResponsiblePerson({
			...rentAgainResponsiblePerson,
			id: event.target.value as string,
		});
	};

	const handleChangeResponsiblePersonDetails = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setRentAgainResponsiblePerson({
			...rentAgainResponsiblePerson,
			[name]: value,
		});
	};

	const handleContinue = async () => {
		setRentAgainShoppers(sortedShoppers.filter((s) => rentAgainShopperIds.includes(s.id)));
		const missingDetailsShoppers = getMissingDetails();
		const { responsiblePerson } = rentalInfo;
		if (missingDetailsShoppers.length === 0) {
			if (
				!responsiblePerson.external &&
				rentAgainShopperIds.includes(responsiblePerson.shopperId)
			) {
				await handleSubmit();
				return;
			}
			setRentAgainResponsiblePerson({
				...rentAgainResponsiblePerson,
				id: rentAgainShopperIds[0],
			});
			setDialogContent('RESPONSIBLE_PERSON');
			return;
		}
		const newMissingDetailsFormError = missingDetailsShoppers.reduce(
			(formError, { shopperId, missingDetails }) => {
				const newMissingDetails = missingDetails.reduce((obj, detail) => {
					obj[detail] = true;
					return obj;
				}, {});
				formError[shopperId] = newMissingDetails;
				return formError;
			},
			{},
		);
		setMissingDetailsShoppers(missingDetailsShoppers);
		setMissingDetailsShopperIndex(0);
		setMissingDetailsFormError(newMissingDetailsFormError);
		setDialogContent('MISSING_DETAILS');
	};

	const handleUpdateMissingDetails = async () => {
		if (missingDetailsShopperIndex < missingDetailsShoppers.length - 1) {
			setMissingDetailsShopperIndex(missingDetailsShopperIndex + 1);
			return;
		}
		const { responsiblePerson } = rentalInfo;
		if (!responsiblePerson.external && rentAgainShopperIds.includes(responsiblePerson.shopperId)) {
			await handleSubmit();
			return;
		}
		setRentAgainResponsiblePerson({
			...rentAgainResponsiblePerson,
			id: rentAgainShopperIds[0],
		});
		setDialogContent('RESPONSIBLE_PERSON');
	};

	const handleUpdateResponsiblePerson = () => {
		handleSubmit(rentAgainResponsiblePerson);
	};

	const handleSubmit = async (newResponsiblePerson?: typeof rentAgainResponsiblePerson) => {
		setLoading(true);
		try {
			const rentAgainOrderObject = getCopiedOrderObject(newResponsiblePerson);
			await setNewOrder(rentAgainOrderObject);

			push(Routes.BookingPage(rentAgainOrderObject.rentalInfo.id));

			onClose(true);
			setLoading(false);
		} catch (e) {
			setLoading(false);
		}
	};

	const getCopiedOrderObject = (
		newResponsiblePerson?: typeof rentAgainResponsiblePerson,
	): OrderObject => {
		const newRentalId = newFirestoreId();
		const startDate = getTimeNearestToNextQuarterHour().toISOString();
		const { responsiblePerson } = rentalInfo;
		let updatedResponsiblePerson: ResponsiblePerson = { ...responsiblePerson };
		const rentAgainProducts: OrderProduct[] = updateOrderProducts({
			products: products.filter((p) => rentAgainShopperIds.includes(p.shopperId)),
			stockProducts,
			shoppers: sortedShoppers,
			shop,
			startDate,
			currencyCode: rentalInfo.pricing.currency,
			keepProductCodes,
			channel: 'ADMIN',
		});
		let updatedRentAgainProducts: OrderProduct[] = [];
		const updatedRentAgainShoppers: Shopper[] = (rentAgainShoppers.length > 0
			? rentAgainShoppers
			: sortedShoppers.filter((s) => rentAgainShopperIds.includes(s.id))
		).map((shopper) => {
			const newShopperId = newFirestoreId();
			const productIds: string[] = [];
			rentAgainProducts.forEach((product) => {
				if (product.shopperId === shopper.id) {
					const newProductId = newFirestoreId();
					const newProduct: OrderProduct = {
						...product,
						charge: {
							paid: 0,
						},
						deposit: {
							paid: 0,
						},
						id: newProductId,
						shopperId: newShopperId,
						rentalId: newRentalId,
					};
					updatedRentAgainProducts.push(newProduct);
					productIds.push(newProductId);
				}
			});
			const updatedShopper: Shopper = {
				...shopper,
				id: newShopperId,
				rentalId: newRentalId,
				additionalInformation: '',
				productsReturned: false,
				created: new Date().toISOString(),
				productIds,
			};
			// If responsible person is in the list, change shopperId to new id
			if (!responsiblePerson.external && responsiblePerson.shopperId === shopper.id) {
				updatedResponsiblePerson = {
					external: false,
					shopperId: newShopperId,
				};
			}
			// If responsible person is not in the list, change shopperId to new id of the selected shopper
			// and add phone, email to that shopper.
			if (newResponsiblePerson && newResponsiblePerson.id === shopper.id) {
				updatedResponsiblePerson = {
					external: false,
					shopperId: newShopperId,
				};
				return {
					...updatedShopper,
					phone: newResponsiblePerson.phone,
					email: newResponsiblePerson.email,
				};
			}
			return updatedShopper;
		});
		const orderPricing = getOrderPricing(
			updatedRentAgainProducts,
			undefined,
			getPricingOptions(rentalInfo.pricing),
		);
		const rentAgainRentalInfo: OrderInfo = {
			...rentalInfo,
			charge: {
				paid: 0,
			},
			deposit: {
				paid: 0,
			},
			shopperIdsWithProducts: getShopperIdsWithProductsFromShoppers(updatedRentAgainShoppers),
			responsiblePerson: updatedResponsiblePerson,
			id: newRentalId,
			activeState: 'ADMIN_BOOKED',
			rentalState: 'BOOKED',
			channel: 'ADMIN',
			endDateReturned: null,
			additionalInformation: '',
			prepared: false,
			rentalProductCodes: getProductCodes(updatedRentAgainProducts),
			startDate,
			created: new Date().toISOString(),
			pricing: orderPricing,
		};
		delete rentAgainRentalInfo.orderNumber;
		if (rentAgainRentalInfo.services) {
			delete rentAgainRentalInfo.services;
		}
		return {
			products: updatedRentAgainProducts,
			shoppers: updatedRentAgainShoppers,
			rentalInfo: rentAgainRentalInfo,
		};
	};

	const getMissingDetails = () =>
		rentAgainShopperIds
			.map((shopperId) => {
				const shopper = sortedShoppers.find((s) => s.id === shopperId);
				if (!shopper) {
					return null;
				}
				const { userProperties } = shopper;
				const updatedRentAgainProducts = updateOrderProducts({
					products: products.filter((p) => rentAgainShopperIds.includes(p.shopperId)),
					stockProducts,
					shoppers: sortedShoppers,
					shop,
					startDate: getTimeNearestToNextQuarterHour().toISOString(),
					currencyCode: rentalInfo.pricing.currency,
					keepProductCodes,
					channel: 'ADMIN',
				});
				const shopperProducts = updatedRentAgainProducts.filter((p) => shopper.id === p.shopperId);
				const details = getSortedRequiredDetails(shopperProducts);
				const missingDetails = details.filter(
					(detail) => !Object.keys(userProperties).includes(detail),
				);
				if (missingDetails.length === 0) {
					return null;
				}
				return {
					shopperId,
					missingDetails,
				};
			})
			.filter((s) => Boolean(s)) as MissingDetails[];

	const renderOverbookingBanner = (hasDelivery?: boolean) => (
		<Box pb={hasDelivery ? 1 : 2}>
			<OverbookedBanner
				allowOverbookings={allowOverbookings}
				rentalProducts={rentAgainProducts}
				openingHours={{
					isOutsideOpeningHours: someRentalOutsideOpeningHours,
				}}
				overbookedVariantIds={overbookedVariantIds}
				description={
					!allowOverbookings && !overbookedVariantIds.length ? (
						<ul className={classes.unstyledBannerList}>
							<li>
								{t('newRental.endsOutsideOpeningHours2', 'Order ends outside of opening hours.')}
							</li>
						</ul>
					) : undefined
				}
			/>
		</Box>
	);

	const renderWhoWantToRentAgain = () => {
		const missingDetailsShoppers = getMissingDetails();
		const { responsiblePerson } = rentalInfo;
		const CTAButtonText =
			missingDetailsShoppers.length === 0 &&
			!responsiblePerson.external &&
			rentAgainShopperIds.includes(responsiblePerson.shopperId)
				? t('common:actions.confirm')
				: t('rentAgain.rentAgain');
		return (
			<>
				<Box className={classes.dialogTitleWrapper}>
					<StripePattern animationSpeed={'slow'} color="dark" opacity={0.2} />
					<Typography variant="h6" className={classes.dialogTitle}>
						{t('rentAgain.chooseParticipants')}
					</Typography>
				</Box>
				<DialogContent className={classes.content}>
					{showBanner && renderOverbookingBanner(hasDelivery)}
					{hasDelivery && (
						<Box className={classes.banner}>
							<Banner variant="info" description={t('rentAgain.deliveryNotAdded')} />
						</Box>
					)}
					{sortedShoppers.map((shopper) => (
						<FormControlLabel
							key={shopper.id}
							control={
								<Checkbox
									id={shopper.id}
									color="primary"
									checked={!!rentAgainShopperIds.includes(shopper.id)}
									onChange={toggleSelectRentAgainShoppers}
									value="noSegments"
								/>
							}
							label={`${shopper.firstName}${shopper.lastName && ` ${shopper.lastName}`}`}
							className={classes.formLabel}
						/>
					))}
					{!!rentAgainShopperIds.length && (
						<div className={classes.productCodeToggle}>
							<FormControlLabel
								style={{ margin: 0 }}
								control={
									<Switch
										color="primary"
										checked={keepProductCodes}
										onChange={(e) => setKeepProductCodes(e.target.checked)}
										value="keepProductCodes"
									/>
								}
								label={t('rentAgain.includeProductCodes', 'Keep the same product codes')}
							/>
						</div>
					)}
				</DialogContent>
				<DialogActions className={classes.paddingActions}>
					<Button onClick={() => onClose()} color="secondary">
						{t('common:actions.cancel')}
					</Button>
					<Button
						disabled={rentAgainShopperIds.length === 0 || loading || DisabledCTADueToOverbooking}
						onClick={handleContinue}
						color="primary"
						variant="contained"
					>
						{loading ? <Spinner padding={8} thickness={4} /> : CTAButtonText}
					</Button>
				</DialogActions>
			</>
		);
	};

	const renderMissingDetailsShoppers = (missingDetailsShopper: MissingDetails) => {
		const { shopperId, missingDetails } = missingDetailsShopper;
		const rentAgainShopper = rentAgainShoppers.find((s) => s.id === shopperId);
		if (!rentAgainShopper) {
			return null;
		}
		const shopperFullName = rentAgainShopper.firstName + ' ' + rentAgainShopper.lastName;
		return (
			<div className={classes.nameContainer}>
				<Grid container xs={12} alignItems="baseline" justifyContent="space-between">
					<Grid item xs={12} sm="auto">
						<Typography variant="subtitle1">{shopperFullName}</Typography>
					</Grid>
					{detailsIncludeMeasurementUnits(missingDetails) && (
						<Grid item xs={12} sm="auto">
							<UnitSystemSelect
								value={unitSystem}
								onChange={(newUnitSystem) => handleUnitSystemChange(newUnitSystem, shopperId)}
								t={t}
							/>
						</Grid>
					)}
				</Grid>
				<Grid container spacing={2}>
					{missingDetails.map((detail) => (
						<Grid item xs={12} key={detail}>
							<UserDetailField
								detail={detail}
								unitSystem={unitSystem}
								userProperty={rentAgainShopper.userProperties?.[detail]}
								onChange={(newValue) => handleDetailChange(newValue, detail, shopperId)}
								t={t}
							/>
						</Grid>
					))}
				</Grid>
			</div>
		);
	};

	const renderAddMissingDetails = () => {
		const { responsiblePerson } = rentalInfo;
		const CTAButtonText =
			missingDetailsShopperIndex === missingDetailsShoppers.length - 1 &&
			!responsiblePerson.external &&
			rentAgainShopperIds.includes(responsiblePerson.shopperId)
				? t('common:actions.confirm')
				: t('common:actions.continue');
		let formError = false;
		const { shopperId } = missingDetailsShoppers[missingDetailsShopperIndex];
		for (const detail in missingDetailsFormError[shopperId]) {
			if (missingDetailsFormError[shopperId][detail]) {
				formError = true;
			}
		}
		return (
			<>
				<Box className={classes.dialogTitleWrapper}>
					<StripePattern animationSpeed={'slow'} color="dark" opacity={0.2} />
					<Typography variant="h6" className={classes.dialogTitle}>
						{t('rentAgain.addMissingDetails', 'Add missing details')}
					</Typography>
				</Box>
				<DialogContent className={classes.content}>
					{showBanner && renderOverbookingBanner()}
					{renderMissingDetailsShoppers(missingDetailsShoppers[missingDetailsShopperIndex])}
				</DialogContent>
				<DialogActions className={classes.paddingActions}>
					<Button onClick={() => onClose()} color="secondary">
						{t('common:actions.cancel')}
					</Button>
					<Button
						disabled={formError || loading || DisabledCTADueToOverbooking}
						onClick={handleUpdateMissingDetails}
						color="primary"
						variant="contained"
					>
						{loading ? <Spinner padding={8} thickness={4} /> : CTAButtonText}
					</Button>
				</DialogActions>
			</>
		);
	};

	const renderAddResponsiblePersonDetails = () => {
		return (
			<>
				<Box className={classes.dialogTitleWrapper}>
					<StripePattern animationSpeed={'slow'} color="dark" opacity={0.2} />
					<Typography variant="h6" className={classes.dialogTitle}>
						{t('rentAgain.addResponsiblePersonDetails', 'Add responsible person details')}
					</Typography>
				</Box>
				<DialogContent className={classes.content}>
					<Grid container spacing={2} justifyContent="space-around">
						{showBanner && renderOverbookingBanner()}
						<Grid item xs={12}>
							<FormControl fullWidth>
								<InputLabel htmlFor="responsible-person-label" variant="standard">
									{t('details.responsiblePerson')}
								</InputLabel>
								<Select
									className={classes.select}
									variant="standard"
									classes={{
										select: classes.selectPadding,
									}}
									inputProps={{
										name: 'responsible-person',
										id: 'responsible-person-label',
									}}
									value={rentAgainResponsiblePerson.id}
									onChange={handleSelectResponsiblePerson}
								>
									{rentAgainShopperIds.map((shopperId) => {
										const shopper = sortedShoppers.find((s) => s.id === shopperId);
										const shopperName =
											shopper &&
											`${shopper.firstName}${shopper.lastName && ` ${shopper.lastName}`}`;
										return (
											<MenuItem key={shopperId} value={shopperId}>
												{shopperName}
											</MenuItem>
										);
									})}
									<MenuItem value="" disabled>
										{t(
											'rentAgain.anotherPersonIsResponsible',
											'Add another person as the responsible person',
										)}
									</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label={t('common:form.phoneNumber', 'Phone number')}
								variant="standard"
								name="phone"
								type="tel"
								value={rentAgainResponsiblePerson.phone}
								onChange={handleChangeResponsiblePersonDetails}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label={t('common:form.email')}
								variant="standard"
								name="email"
								type="email"
								value={rentAgainResponsiblePerson.email}
								onChange={handleChangeResponsiblePersonDetails}
								fullWidth
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions className={classes.paddingActions}>
					<Button onClick={() => onClose()} color="secondary">
						{t('common:actions.cancel')}
					</Button>
					<Button
						disabled={
							!rentAgainResponsiblePerson.phone || !rentAgainResponsiblePerson.email || loading
						}
						onClick={handleUpdateResponsiblePerson || DisabledCTADueToOverbooking}
						color="primary"
						variant="contained"
					>
						{loading ? <Spinner padding={8} thickness={4} /> : t('common:actions.confirm')}
					</Button>
				</DialogActions>
			</>
		);
	};

	const renderDialogContent = () => {
		switch (dialogContent) {
			case 'WHO_RENT':
				return renderWhoWantToRentAgain();
			case 'MISSING_DETAILS':
				return renderAddMissingDetails();
			case 'RESPONSIBLE_PERSON':
				return renderAddResponsiblePersonDetails();
			default:
				return renderWhoWantToRentAgain();
		}
	};

	return (
		<Dialog open={open} onClose={() => onClose()} classes={{ paper: classes.paper }}>
			{renderDialogContent()}
		</Dialog>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		paper: {
			margin: 0,
			[theme.breakpoints.up('sm')]: {
				width: 460,
			},
		},
		content: {
			display: 'grid',
			padding: theme.spacing(3, 4, 1, 4),
		},
		formLabel: {
			margin: 0,
			'&:hover': {
				backgroundColor: theme.palette.action.hover,
			},
		},
		paddingActions: {
			padding: theme.spacing(1, 3, 3),
		},
		selectPadding: {
			paddingRight: theme.spacing(3),
		},
		select: {
			color: theme.palette.common.black,
			fontSize: '11pt',
		},
		nameContainer: {
			marginBottom: theme.spacing(3),
		},
		productCodeToggle: {
			marginTop: theme.spacing(2),
		},
		banner: {
			marginBottom: theme.spacing(3),
		},
		dialogTitleWrapper: {
			padding: theme.spacing(2),
			backgroundColor: theme.palette.primary.main,
			position: 'relative',
			overflow: 'hidden',
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start',
		},
		dialogTitle: {
			position: 'relative',
			zIndex: 2,
			color: theme.palette.primary.contrastText,
			backgroundColor: theme.palette.primary.main,
			padding: theme.spacing(0, 1),
			borderRadius: '30px',
		},
		unstyledBannerList: {
			listStyle: 'none',
			paddingLeft: 0,
			margin: 0,
		},
	});

const mapStateToProps = ({ shop, stock }: ReduxState): StateToProps => {
	return {
		shop: shop.shop.data!,
		stockProducts: stock.stockProducts.data!,
	};
};

export default withStyles(styles)(connect(mapStateToProps)(RentAgainDialog));
